import type { VariantProps } from "class-variance-authority"
import { cva } from "class-variance-authority"

export { default as Button } from "./Button.vue"

// Paper buttons: hand-cut corners (--paper-radius-sm), filled variants are card
// stock with grain + the --paper-lift thickness (lit rim, 3px lip, short cast);
// pressed = pushed flat (translate-y-px + --paper-lift-pressed). No glow.
// Tailwind v4 `translate-y-px` sets the `translate` property, so that (not
// `transform`) is in the transition list.
// `torn` is a small scissor-cut CTA chip: the fill lives on ::before (clipped by
// --paper-cut-btn — deep 2–3px snips, 3–4px corner nicks, so it reads as cut at
// 390px) and its shadow is a drop-shadow on the button itself — the wrapper of
// the clipped piece — so it also works with `as-child`. Single CTA chips only,
// never inside a list.
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--paper-radius-sm)] text-sm font-medium transition-[color,background-color,box-shadow,translate,opacity] duration-150 ease-out active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-paper-stock/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "paper-grain bg-primary text-primary-foreground shadow-(--paper-lift) hover:bg-primary/90 active:shadow-(--paper-lift-pressed)",
        destructive:
          "paper-grain bg-destructive text-destructive-foreground shadow-(--paper-lift) hover:bg-destructive/90 active:shadow-(--paper-lift-pressed) focus-visible:ring-destructive/40",
        outline:
          "border border-foreground/25 bg-transparent hover:bg-foreground/5 hover:border-foreground/40",
        secondary:
          "paper-tag shadow-(--paper-lift) hover:bg-surface-200 active:shadow-(--paper-lift-pressed)",
        ghost:
          "hover:bg-foreground/5",
        link: "text-primary-ink underline-offset-4 hover:underline",
        success:
          "paper-grain bg-success text-success-foreground shadow-(--paper-lift) hover:bg-success/90 active:shadow-(--paper-lift-pressed)",
        warning:
          "paper-grain bg-warning text-warning-foreground shadow-(--paper-lift) hover:bg-warning/90 active:shadow-(--paper-lift-pressed)",
        info:
          "paper-grain bg-info text-info-foreground shadow-(--paper-lift) hover:bg-info/90 active:shadow-(--paper-lift-pressed)",
        torn:
          "relative isolate rounded-none bg-transparent text-primary-foreground [filter:drop-shadow(0_3px_0_rgb(4_3_18/0.5))_drop-shadow(0_6px_6px_rgb(4_3_18/0.35))] before:absolute before:inset-0 before:-z-10 before:bg-primary before:bg-(image:--paper-grain) before:bg-size-[160px_160px] before:[clip-path:var(--paper-cut-btn)] before:transition-colors hover:before:bg-primary/90 active:[filter:drop-shadow(0_1px_0_rgb(4_3_18/0.5))]",
      },
      size: {
        "default": "h-11 px-4 py-2 has-[>svg]:px-3",
        "sm": "h-9 gap-1.5 px-3 has-[>svg]:px-2.5",
        "lg": "h-12 px-6 has-[>svg]:px-4",
        "icon": "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
